import {
  Box,
  ButtonBase,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  styled,
} from '@mui/material'

import LinkChild from '@src/lib/LinkChild'
import { ThemeAwareColor } from '@src/lib/theme'
import { numberFormatter } from '@src/lib/Utils'

import Sash, { SashProps } from '../shared/Sash'

export interface CampaignCardProps {
  href?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  isLegacyLink?: boolean
  dim?: boolean
  slotProps?: {
    sash?: SashProps
  }
}
export const CampaignCard: React.FC<
  React.PropsWithChildren<CampaignCardProps>
> = ({ children, slotProps, isLegacyLink, dim, ...props }) => {
  const buttonChild = (
    <ButtonBase
      // The strange destructing of props is due to how ButtonBase acts when you
      // pass it a potentially empty `href`. TypeScript does not like that.
      // However, it doesn't seem to happen when you spread in props.
      {...props}
      // Hack to help Cypress. Skeletons use this component, and Cypress assumes
      // loading is done when it finds an element with this test id.
      // Skeletons don't set an href or onClick so ...
      data-testid={
        props.href || props.onClick ? 'CampaignCardButton' : undefined
      }
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        textAlign: 'unset',
        opacity: dim ? 0.5 : undefined,
      }}
    >
      <Box width="100%">{children}</Box>
    </ButtonBase>
  )

  let child: React.ReactNode

  if (props.href) {
    if (isLegacyLink) {
      child = buttonChild
    } else {
      child = <LinkChild to={props.href}>{buttonChild}</LinkChild>
    }
  } else {
    child = buttonChild
  }

  return (
    <>
      <Paper
        elevation={6}
        className="CampaignCard-root"
        sx={(theme) => ({
          overflow: 'hidden',

          // For the sash.
          position: 'relative',

          // TODO Can be removed when we start using `CssBaseline`.
          fontFamily: theme.typography.fontFamily,
        })}
      >
        {child}

        {slotProps?.sash?.label ? <Sash {...slotProps.sash} /> : undefined}
      </Paper>
    </>
  )
}

export interface CampaignCardHeaderProps {
  title?: React.ReactNode
  subheader?: React.ReactNode
}
export const CampaignCardHeader: React.FC<CampaignCardHeaderProps> = (
  props,
) => {
  return (
    <Box
      // min-width of 0 is needed for ellipsis to work in flex.
      // https://css-tricks.com/flexbox-truncated-text/
      minWidth={0}
    >
      {props.title ? (
        <Typography variant="body1" fontWeight="bold" noWrap>
          {props.title}
        </Typography>
      ) : undefined}

      {props.subheader ? (
        <Typography variant="body2" color="text.secondary">
          {props.subheader}
        </Typography>
      ) : undefined}
    </Box>
  )
}

export interface CampaignCardContentProps {}
export const CampaignCardContent: React.FC<
  React.PropsWithChildren<CampaignCardContentProps>
> = (props) => {
  return (
    <Box
      padding={4}
      // Paid Ads can show an "available funds" message that uses absolute
      // positioning to avoid breaking the height.
      position="relative"
    >
      {props.children}
    </Box>
  )
}

export interface CampaignCardSecondaryContentProps {}
export const CampaignCardSecondaryContent: React.FC<
  React.PropsWithChildren<CampaignCardSecondaryContentProps>
> = (props) => {
  return (
    <>
      <Divider />

      <Box paddingX={4} paddingY={2}>
        {props.children}
      </Box>
    </>
  )
}

const Vignette = styled('div')(() => ({
  boxShadow: 'rgba(0, 0, 0, 0.15) 0px 0px 25px 10px inset',

  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
}))

const CampaignCardMediaImage = styled('img')(({ theme }) => ({
  'transition': theme.transitions.create('transform', {
    easing: theme.transitions.easing.easeInOut,
    duration: 10_000,
  }),

  '&:hover, .CampaignCard-root:hover &': {
    transform: 'scale(1.1)',
  },
}))

export interface CampaignCardMediaProps {
  src?: string
}
export const CampaignCardMedia: React.FC<CampaignCardMediaProps> = (props) => {
  if (!props.src) {
    return null
  }

  return (
    <Box height="180px" position="relative" overflow="hidden">
      <CampaignCardMediaImage
        src={props.src}
        alt="Campaign"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <Vignette />
    </Box>
  )
}

export interface CampaignCardActionAreaProps {
  color?: ThemeAwareColor
  invert?: boolean
}
export const CampaignCardActionArea: React.FC<
  React.PropsWithChildren<CampaignCardActionAreaProps>
> = (props) => {
  const bgColor = props.invert ? undefined : `${props.color}.main`
  const textColor = props.invert
    ? `${props.color}.main`
    : `${props.color}.contrastText`
  const bgColorHover = props.invert ? `grey.100` : `${props.color}.dark`
  const textColorHover = props.invert
    ? `${props.color}.main`
    : `${props.color}.contrastText`

  return (
    <>
      <Divider />

      <Stack
        direction="row"
        spacing={2}
        padding={4}
        bgcolor={bgColor}
        color={textColor}
        alignItems="center"
        justifyContent="center"
        sx={{
          '&:hover, .CampaignCard-root:hover &': {
            bgcolor: bgColorHover,
            color: textColorHover,
          },
        }}
      >
        {props.children}
      </Stack>
    </>
  )
}

export const CampaignCardActionAreaDot = styled('div')(({ theme }) => ({
  backgroundColor: 'currentcolor',
  width: 16,
  height: 16,
  border: `1px solid ${theme.palette.grey[500]}`,
  borderRadius: '50%',
}))

export const CampaignCardTruncatedDescription: React.FC<
  React.PropsWithChildren
> = (props) => {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        // Arbitrary number based on the height of one line.
        height: 20 * 3,

        // All this is needed for line-clamp to work.
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        display: '-webkit-box',
      }}
    >
      {props.children}
    </Typography>
  )
}

export const CampaignCardSkeleton: React.FC = () => {
  return (
    <CampaignCard>
      <Skeleton height={180} variant="rectangular" />

      <CampaignCardContent>
        <CampaignCardHeader
          title={<Skeleton width="80%" />}
          subheader={<Skeleton width="40%" />}
        />
      </CampaignCardContent>

      <CampaignCardSecondaryContent>
        <Skeleton />
      </CampaignCardSecondaryContent>

      <CampaignCardActionArea color="pbxxCyan" invert>
        <Skeleton width="30%" />
      </CampaignCardActionArea>
    </CampaignCard>
  )
}

export interface CampaignCardQuickStatProps {
  number?: number | null
  label: React.ReactNode
}
export const CampaignCardQuickStat: React.FC<CampaignCardQuickStatProps> = (
  props,
) => {
  return (
    <>
      <Stack direction="column" alignItems="center">
        <Typography variant="h6">
          {props.number == null ? '-' : numberFormatter().format(props.number)}
        </Typography>
        <Typography variant="overline" color="text.secondary">
          {props.label}
        </Typography>
      </Stack>
    </>
  )
}
